import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';

const teamMembers = [
  {
    name: 'KAMBUA MBUKU Jack',
    role: 'Avocat',
    photo: 'images/pic/1.png',
    bio: 'Expert en droit des sociétés et contrats commerciaux, j’aide entreprises et entrepreneurs à sécuriser leurs opérations et anticiper leurs enjeux juridiques.',
    expertise: ['Sociétés', 'Contrats', 'Gouvernance']
  },
  {
    name: 'KILOLO DISASI Josué',
    role: ' Avocat Associé',
    photo: 'images/pic/2.jpeg',
    bio: 'Expert en droit pénal des affaires et stratégie juridique complexe, assurant une défense rigoureuse et proactive des intérêts de nos clients.',
    expertise: ['Pénal des affaires', 'Contentieux', 'Médiation']
  },
  {
    name: 'EFAMBE YOLANGA Paul Jr',
    role: 'Consultant International',
    photo: 'images/pic/4.jpeg',
    bio: 'Consultant en stratégie globale, j’accompagne organisations et gouvernements dans l’analyse, la planification et l’exécution de solutions à fort impact.',
    expertise: ['Stratégie', 'International', 'Conformité']
  },
  {
    name: 'BONONGA FREDY Ali',
    role: 'Avocat Collaborateur',
    photo: 'images/pic/3.jpeg',
    bio: 'Défenseur passionné des droits fondamentaux, j’accompagne chaque client avec rigueur, stratégie et détermination pour obtenir la justice qu’il mérite.',
    expertise: ['Droits Humains', 'Civique', 'Plaidoyer']
  },
];

const TeamSection = () => {
  useEffect(() => {
    const cards = document.querySelectorAll('.team-card');
    cards.forEach((card, index) => {
      card.style.animationDelay = `${index * 0.15}s`;
    });
  }, []);

  return (
    <Section>
      {/* Texture de fond */}
      <NoiseOverlay />

      <SectionHeader>
        <SectionBadge>Nos Experts</SectionBadge>
        <SectionTitle>Notre Équipe</SectionTitle>
        <IntroText>
          Notre cabinet s'appuie sur des avocats, juristes et consultants hautement qualifiés. 
          Avec rigueur et excellence, nous unissons nos expertises pour sécuriser vos opérations 
          et défendre vos intérêts devant toutes les juridictions.
        </IntroText>
      </SectionHeader>

      <Grid>
        {teamMembers.map((member, index) => (
          <Card key={index} className="team-card">
            <ImageContainer>
              <MemberImage src={member.photo} alt={member.name} loading="lazy" />
              <ImageOverlay />
            </ImageContainer>
            <Content>
              <Name>{member.name}</Name>
              <Role>{member.role}</Role>
              <Bio className="member-bio">
                <BioText>{member.bio}</BioText>
                <Expertise>
                  {member.expertise.map((item, i) => (
                    <ExpertiseItem key={i}>{item}</ExpertiseItem>
                  ))}
                </Expertise>
              </Bio>
            </Content>
            <CardBorder className="card-border" />
          </Card>
        ))}
      </Grid>
      
      <ButtonContainer>
        <StyledLink to="/Team">
          Voir toute l'équipe
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 12h14M12 5l7 7-7 7" />
          </svg>
        </StyledLink>
      </ButtonContainer>
    </Section>
  );
};

// Animations
const cardEntrance = keyframes`
  from { opacity: 0; transform: translateY(40px); }
  to { opacity: 1; transform: translateY(0); }
`;

// Styled-components
const Section = styled.section`
  padding: 8rem 5%;
  background-color: #0A0A0A; /* Dark Gray de base */
  position: relative;
  overflow: hidden;

  /* Fond image très subtil */
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image: url('images/bg2.jpg');
    background-position: center;
    background-size: cover;
    opacity: 0.1; /* Très sombre pour ne pas déranger */
    filter: grayscale(100%);
    z-index: 0;
  }
`;

const NoiseOverlay = styled.div`
  position: absolute;
  inset: 0;
  background-image: url('https://www.transparenttextures.com/patterns/stardust.png');
  opacity: 0.15;
  mix-blend-mode: overlay;
  pointer-events: none;
  z-index: 1;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 5rem;
  position: relative;
  z-index: 2;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
`;

const SectionBadge = styled.span`
  display: inline-block;
  border: 1px solid #C1A68D;
  color: #C1A68D;
  padding: 0.5rem 1.2rem;
  border-radius: 4px;
  font-size: 0.85rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 1.5rem;
  background: rgba(193, 166, 141, 0.05);
`;

const SectionTitle = styled.h2`
  font-size: clamp(2.5rem, 5vw, 3.5rem);
  color: #F5F0E6;
  text-transform: uppercase;
  letter-spacing: 2px;
  margin-bottom: 1.5rem;
  font-weight: 700;
`;

const IntroText = styled.p`
  color: #D4C5B0;
  font-size: 1.1rem;
  line-height: 1.7;
  font-weight: 300;

  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  max-width: 1400px;
  margin: 0 auto;
  position: relative;
  z-index: 2;

  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 1.5rem;
  }
`;

const Card = styled.div`
  position: relative;
  border-radius: 4px;
  overflow: hidden;
  background-color: #161616;
  opacity: 0;
  animation: ${cardEntrance} 0.8s ease forwards;
  border: 1px solid rgba(193, 166, 141, 0.1);

  &:hover {
    .member-bio {
      max-height: 500px;
      opacity: 1;
      margin-top: 1rem;
    }

    .card-border {
      opacity: 1;
    }
  }
`;

const ImageContainer = styled.div`
  position: relative;
  height: 480px;
  overflow: hidden;

  @media (max-width: 768px) {
    height: 420px;
  }
`;

const MemberImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center top;
  transition: transform 0.8s ease, filter 0.8s ease;
  filter: grayscale(40%) contrast(110%);

  ${Card}:hover & {
    transform: scale(1.05);
    filter: grayscale(0%) contrast(110%);
  }
`;

const ImageOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    to top,
    rgba(10, 10, 10, 1) 0%,
    rgba(10, 10, 10, 0.7) 35%,
    rgba(10, 10, 10, 0) 100%
  );
`;

const Content = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 2rem;
  z-index: 2;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
`;

const Name = styled.h3`
  font-size: 1.4rem;
  color: #F5F0E6;
  margin-bottom: 0.3rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const Role = styled.p`
  color: #C1A68D;
  font-size: 0.95rem;
  font-weight: 400;
  letter-spacing: 0.5px;
  margin-bottom: 0;
`;

const Bio = styled.div`
  max-height: 0;
  overflow: hidden;
  opacity: 0;
  transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
`;

const BioText = styled.p`
  color: #D4C5B0;
  font-size: 0.9rem;
  line-height: 1.6;
  margin-bottom: 1.2rem;
  font-weight: 300;
  border-top: 1px solid rgba(193, 166, 141, 0.2);
  padding-top: 1rem;
`;

const Expertise = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const ExpertiseItem = styled.span`
  background: transparent;
  color: #C1A68D;
  padding: 0.3rem 0.6rem;
  border-radius: 2px;
  font-size: 0.75rem;
  border: 1px solid rgba(193, 166, 141, 0.3);
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const CardBorder = styled.div.attrs({ className: 'card-border' })`
  position: absolute;
  inset: 0;
  border: 1px solid #C1A68D;
  border-radius: 4px;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.4s ease;
  z-index: 3;
`;

const ButtonContainer = styled.div`
  text-align: center;
  margin-top: 5rem;
  position: relative;
  z-index: 2;
`;

const StyledLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  background-color: transparent;
  color: #C1A68D;
  padding: 1rem 2.5rem;
  border-radius: 4px;
  font-weight: 500;
  font-size: 1rem;
  text-transform: uppercase;
  letter-spacing: 1px;
  text-decoration: none;
  border: 1px solid #C1A68D;
  transition: all 0.3s ease;

  svg {
    width: 18px;
    height: 18px;
    transition: transform 0.3s ease;
  }

  &:hover {
    background-color: #C1A68D;
    color: #1A1A1A;

    svg {
      transform: translateX(5px);
    }
  }
`;

export default TeamSection;